/* eslint-disable no-param-reassign */
/* eslint-disable no-unused-expressions */
import { ROCKWELL_BREAKPOINTS_MAX_EM } from 'js/constants.js';
import { fillTemplate } from '../common/filters/fill-template.js';
export { fillTemplate } from '../common/filters/fill-template.js';
export { handlePagination, updatePagination } from '../common/filters/pagination.js';

/**
 * Filter facets (used for search and URL params)
 * NOTE: these need the full field name, including suffix (e.g. "_ss")
 */
export const FilterFacets = Object.freeze({
    type: 'rl_programType_ss', // Full type (e.g. SI Silver)
    ptyp: 'rl_partner_type_s', // Type (e.g. SI, OEM)
    styp: 'rl_partner_subtype_ss', // Subtype (e.g. Silver, Is Var)
    dist: 'rl_distributor_level_ss', // Distributor Subtype (e.g. Is Var)
    capa: 'rl_disciplines_ss', // Capability
    indu: 'rl_industries_ss', // Industry
    prod: 'rl_prod_category_ss', // Product Category
    mach: 'rl_machine_seg_ss', // Machine Segment
    msub: 'rl_machine_subseg_ss', // Machine Sub-Segment
    cert: 'rl_certifications_ss', // Certification
    appl: 'rl_applications_ss', // Application
    plat: 'rl_prod_platforms_ss', // Platform
    conn: 'rl_prod_connectivities_ss', // Connectivity
});

export const OtherUrlParams = Object.freeze({
    accountId: 'id',
    productId: 'pid',
    isSalesOffice: 'so',
    aprCode: 'ac',
});

/**
 * API field name mapping
 * - Note: as long as these field values are used with the getter functions,
 *   they do not need Lucidworks suffixes
 * @param {string} prefix
 */
const generateLocationFields = (prefix) => ({
    Id: `${prefix}.Id`,
    Name: `${prefix}.Name`,
    AddressLine1: `${prefix}.AddressLine1`,
    AddressLine2: `${prefix}.AddressLine2`,
    AddressLine3: `${prefix}.AddressLine3`,
    City: `${prefix}.City`,
    StateProvince: `${prefix}.stateProvince`,
    StateProvince2: `${prefix}.StateProvince`, // For sales offices
    PostalCode: `${prefix}.Postalcode`,
    Country: `${prefix}.Country`,
    Country2: `${prefix}.country`, // For contacts
    Email: `${prefix}.Email`,
    Phone: `${prefix}.Phone`,
    Url: `${prefix}.url`,
    Latitude: `${prefix}.Latitude`,
    Longitude: `${prefix}.Longitude`,
    SalesOfficeBranchId: `${prefix}.SalesOfficeBranchId`,
    AprId: `${prefix}.APRId`,
    Regions: {
        IsAmericas: `${prefix}.IsAmericas`,
        IsLatinAmerica: `${prefix}.IsLatinAmerica`,
        IsEmea: `${prefix}.IsEmea`,
        IsAsia: `${prefix}.IsAsia`,
    }
});

export const Fields = Object.freeze({
    Id: 'id',
    Name: 'partner.mainPartnerInfos.Name',
    LogoId: 'partner.mainPartnerInfos.partnerLogoId',
    ProgramType: 'rl_programType', // e.g. SI Gold
    PartnerType: 'rl_partner_type', // e.g. SI
    PartnerSubtype: 'rl_partner_subtype', // e.g. Gold
    PrimaryLocation: generateLocationFields('partner.primaryLocation'),
    PrimaryLocationId: 'partner.mainPartnerInfos.PrimaryLocation',
    Url: 'partner.mainPartnerInfos.Url',
    FacebookUrl: 'partner.mainPartnerInfos.FacebookUrl',
    LinkedInUrl: 'partner.mainPartnerInfos.LinkedInUrl',
    TwitterUrl: 'partner.mainPartnerInfos.TwitterUrl',
    YouTubeUrl: 'partner.mainPartnerInfos.YouTubeUrl',
    Phone: 'partner.mainPartnerInfos.Phone',
    Description: 'partner.mainPartnerInfos.Description',
    Industries: 'partner.industries.Name',
    IndustryBadgeFlags: 'partner.industries.IndustriesShowBadgeOnLocator',
    MachineSegments: 'partner.machineSegments.Name',
    MachineSubSegments: 'partner.machineSubSegments.machineSubSegments_Name',
    Certifications: 'partner.certifications.Name', // Also contains Affiliations
    CertificationsUrl: 'partner.certifications.Url',
    CertificationsType: 'partner.certifications.Type', // 100000001 = Certification, 100000000 = Affiliation
    SuccessStories: 'partner.successStories.Description',
    SuccessStoriesUrl: 'partner.successStories.Url',
    Disciplines: 'partner.disciplines.Name',
    DisciplinesGroup: 'partner.disciplines.group',
    ProductCategories: 'partner.products.productcategoryName',
    YearEstablished: 'partner.mainPartnerInfos.YearEstablished',
    TotalEmployees: 'partner.mainPartnerInfos.TotalEmployees',
    TotalEngineers: 'partner.mainPartnerInfos.TotalEngineers',
    Distributor: {
        Name: 'partner.mainDistributorInfos.Name',
        AprCode: 'partner.mainDistributorInfos.AprCode',
        Subtype: 'partner.mainDistributorInfos.DistributorLevelText',
        LevelCode: 'partner.mainDistributorInfos.DistributorLevelCode',
        AprUrl: 'partner.mainDistributorInfos.AprUrl',
        Email: 'partner.mainDistributorInfos.Email',
        Phone: 'partner.mainDistributorInfos.MainPhone',
        LineCardUrl: 'partner.mainDistributorInfos.LineCardUrl',
        FacebookUrl: 'partner.mainDistributorInfos.FacebookUrl',
        LinkedInUrl: 'partner.mainDistributorInfos.LinkedInUrl',
        TwitterUrl: 'partner.mainDistributorInfos.TwitterUrl',
        YouTubeUrl: 'partner.mainDistributorInfos.YouTubeUrl',
        LocationId: 'partner.mainDistributorInfos.LocationId',
        LocationPhone: 'partner.mainDistributorInfos.LocationPhone',
        Location: generateLocationFields('partner.mainDistributorInfos'),
    },
    TechPartner: { // Technology Partner specific fields
        CompanyOverviewTitle: 'partner.mainPartnerInfos.CompanyOverviewTitle',
        ValuesStatementSection: 'partner.mainPartnerInfos.ValuesStatementSection',
        PortfolioOffering: 'partner.mainPartnerInfos.TechnologyPartnerPortfolioOffering',
        ValueToCustomer1: 'partner.mainPartnerInfos.ValueToCustomer1',
        ValueToCustomer2: 'partner.mainPartnerInfos.ValueToCustomer2',
        ValueToCustomer3: 'partner.mainPartnerInfos.ValueToCustomer3',
        KeyTakeaways1: 'partner.mainPartnerInfos.KeyTakeaways1',
        KeyTakeaways2: 'partner.mainPartnerInfos.KeyTakeaways2',
        KeyTakeaways3: 'partner.mainPartnerInfos.KeyTakeaways3',
        ShowImageOnLocator: 'partner.mainPartnerInfos.ShowImageOnLocator',
    },
    SalesOffice: {
        BranchId: 'partner.salesofficeInfos.BranchId',
        Name: 'partner.salesofficeInfos.Name',
        DisplayName: 'partner.salesofficeInfos.DisplayName',
        Email: 'partner.salesofficeInfos.Email',
        MainPhone: 'partner.salesofficeInfos.MainPhone',
        ServicePhone: 'partner.salesofficeInfos.ServiceDispatch',
        SupportPhone: 'partner.salesofficeInfos.LocalTechSupport',
        PartsPhone: 'partner.salesofficeInfos.PartsRepairExchange',
        Fax: 'partner.salesofficeInfos.FaxNumber',
        Location: generateLocationFields('partner.salesofficeInfos')
    },
    Products: {
        Id: 'partner.products.ProductId',
        Name: 'partner.products.ProductName',
        CatalogNumber: 'partner.products.CatalogNumber',
        PhotoId: 'partner.products.productPhoto.productId',
        Description: 'partner.products.ProductDescription',
        DescriptionTypo: 'partner.products.ProductDecription', // TEMP
        Overview: 'partner.products.ProductOverview',
        Category: 'partner.products.productcategoryName',
    },
    Contacts: {
        Id: 'partner.contacts.Id',
        FirstName: 'partner.contacts.FirstName',
        LastName: 'partner.contacts.LastName',
        Email: 'partner.contacts.Email',
        Phone: 'partner.contacts.Phone',
        Location: generateLocationFields('partner.contacts'),
        Regions: {
            IsAmericas: 'partner.contacts.Americas',
            IsLatinAmerica: 'partner.contacts.LatinAmerica',
            IsAsia: 'partner.contacts.Asia',
            IsEmea: 'partner.contacts.Emea',
        },
    },
    Locations: generateLocationFields('partner.locations'),
});

export const PartnerTypes = Object.freeze({
    Distributor: 'Authorized Distributor',
    OEM: 'OEM',
    SI: 'SI',
    TechnologyPartner: 'Technology Partner',
    EPCPartner: 'EPC Partner',
    SalesOffice: 'Sales Office',
});

// ==========
// CONFIG
// ==========
let sharedConfig;
/**
 * Get config values provided by LocatorConfigProvider.java
 * @returns {DOMStringMap?} */
export const getConfig = () => {
    if (!sharedConfig) {
        sharedConfig = document.querySelector('.locator-shared__config')?.dataset;
    }
    return sharedConfig;
};

const logoFolder = '/etc.clientlibs/rockwell-aem-base/clientlibs/clientlib-base/resources/logos';
export const LogoMap = Object.freeze({
    'Authorized Distributor': `${logoFolder}/authorized-distributor.svg`,
    'Authorized Distributor ': `${logoFolder}/authorized-distributor.svg`,
    'Authorized Distributor Specialty': `${logoFolder}/authorized-distributor.svg`,
    'Authorized Distributor Value-Add': `${logoFolder}/authorized-distributor.svg`,
    'Authorized Distributor Is Var': `${logoFolder}/authorized-distributor.svg`,
    'OEM Silver': `${logoFolder}/oem-partner-silver.svg`,
    'OEM Gold': `${logoFolder}/oem-partner-gold.svg`,
    'OEM Platinum': `${logoFolder}/oem-partner-platinum.svg`,
    'SI Silver': `${logoFolder}/system-integrator-silver.svg`,
    'SI Gold': `${logoFolder}/system-integrator-gold.svg`,
    'SI Platinum': `${logoFolder}/system-integrator-platinum.svg`,
    'Technology Partner': `${logoFolder}/technology-partner.svg`,
});

/** Map partner types/subtypes to display names */
export const partnerTypeDisplayNameMap = Object.freeze({
    SI: 'System Integrator',
    'Sales Office': 'Rockwell Automation Sales Office',
});

export const CertificationTypes = Object.freeze({
    Certification: 100000001,
    Affiliation: 100000000,
});

export const CapabilitiesGroups = Object.freeze({
    ProjectLifecycle: 'projectlifecyclecapability',
    Design: 'rockwelltechnologiesdesigncapability',
});

// ==========
// GET FIELDS
// ==========
/**
 * @param {Object} apiData
 * @param {string} fieldName
 * @returns {any[]}
 */
export const getFieldAsArray = (apiData, fieldName) => {
    // Solar adds suffixes to certain fields, we want to ignore those
    const apiSuffixes = ['', '_t', '_txt', '_s', '_ss', '_d', '_ds', '_i', '_is', '_b', '_bs'];
    // eslint-disable-next-line no-restricted-syntax
    for (const suffix of apiSuffixes) {
        let value = apiData[fieldName + suffix];
        if (value !== undefined) {
            if (!Array.isArray(value)) {
                value = [value];
            }
            return value.map((v) => (v === 'n/a' ? null : v));
        }
    }
    return [];
};

/**
 * @param {Object} apiData
 * @param {string} fieldName
 */
export const getField = (apiData, fieldName, arrayIndex = 0) => getFieldAsArray(apiData, fieldName)[arrayIndex];

/**
 * @param {Object} apiData
 * @param {Record<string, string>} address
 */
export const getAddressString = (
    apiData,
    {
        AddressLine1,
        AddressLine2,
        AddressLine3,
        City,
        StateProvince,
        StateProvince2,
        PostalCode,
        Country,
        Country2,
    },
    arrayIndex = 0,
    shortFormat = false
) => {
    const boundGetField = (fieldName) => getField(apiData, fieldName, arrayIndex);

    const addressLine1 = boundGetField(AddressLine1);
    const addressLine2 = boundGetField(AddressLine2);
    const addressLine3 = boundGetField(AddressLine3);
    const city = boundGetField(City);
    const stateProvince = boundGetField(StateProvince) ?? boundGetField(StateProvince2);
    const postalCode = boundGetField(PostalCode);
    const country = boundGetField(Country) ?? boundGetField(Country2);

    /**
     * Format helper
     * @param {string} string
     */
    const f = (string, suffix = '') => (string != null && string.trim() !== '' ? string + suffix : '');

    if (shortFormat) {
        return f(city, ', ') + f(stateProvince, ' ') + f(country);
    }
    return (
        f(addressLine1, '<br>')
        + f(addressLine2, '<br>')
        + f(addressLine3, '<br>')
        + f(city, ', ') + f(stateProvince, ' ') + f(postalCode) + ((city || stateProvince || postalCode) ? '<br>' : '')
        + f(country)
    );
};

/**
 * Takes an array of region fields, evaluates their specific values within apiData,
 * and returns the display names of all regions fields that are `true`.
 * @param {Object} apiData
 * @param {Record<string, string>} regionFields
 */
export const getRegions = (apiData, regionFields, arrayIndex = 0) => {
    const regionDict = {
        IsAmericas: 'North America',
        IsLatinAmerica: 'Latin America',
        IsEmea: 'EMEA',
        IsAsia: 'Asia Pacific',
    };
    /** @type {string[]} */
    const regions = [];
    Object.entries(regionDict).forEach(([regionField, regionDisplayName]) => {
        if (getField(apiData, regionFields[regionField], arrayIndex)) {
            regions.push(regionDisplayName);
        }
    });
    return regions;
};

// ==========
// API CALL
// ==========
/** @param {Record<string, string | number>} searchParams */
export const fetchData = async (searchParams, options = {}) => {
    const apiUrl = getConfig().partnerSearchUrl;
    const headers = {
        client_id: getConfig().partnerSearchClientId,
        client_secret: getConfig().partnerSearchClientSecret,
        user_id: getConfig().partnerSearchUserId,
        user_email: getConfig().partnerSearchUserEmail,
        correlation_id: getConfig().partnerSearchCorrelationId,
    };
    const defaultSearchParams = {
        queryText: '',
        startIndex: 0,
        numResults: 1,
        role: 'comm_partners',
        segments: 'comm_partners',
        // collections: 'comm_partners',
        // spellingCorrect: true,
        // spellcheckPremium: 10,
        // languages: 'en',
    };

    const url = new URL(apiUrl);
    const allSearchParams = { ...defaultSearchParams, ...searchParams };
    Object.entries(allSearchParams)
        .filter(([, value]) => value != null)
        .forEach(([key, value]) => url.searchParams.append(key, value));

    const response = await fetch(url, { headers, ...options });
    if (!response.ok) {
        throw new Error(`Failed to fetch data from locator search API. Status: ${response.status} (${response.statusText}).`);
    }
    return response.json();
};

// ==========
// GET IMAGES
// ==========
/**
 * @param {URL} url
 * @param {RequestInit} options
 * @returns {Promise<string?>}
 */
const getImage = async (url, options) => {
    try {
        const response = await fetch(url, options);
        if (response.ok) {
            const base64Image = await response.text(); // API returns base64 encoded data string
            if (base64Image && base64Image.length > 64) {
                const dataUrl = `data:image/jpeg;base64,${base64Image}`;
                return dataUrl;
            }
        }
    } catch {
        // Error case handled below
    }
    return null;
};

/**
 * @param {string} url
 * @param {string} id
 */
const replaceRequestId = (url, id) => url
    .replaceAll('{id_with_dash}', id)
    .replaceAll('{id_without_dash}', id.replaceAll('-', ''));

/**
 * @param {string?} productPhotoId
 * @returns {Promise<string?>}
 */
export const getProductImage = async (productPhotoId) => {
    if (productPhotoId) {
        const url = new URL(replaceRequestId(getConfig().productPhotoUrl, productPhotoId));

        const headers = {
            'Content-Type': 'application/json',
            correlation_id: getConfig().productPhotoCorrelationId,
            client_id: getConfig().productPhotoClientId,
            client_secret: getConfig().productPhotoClientSecret,
        };

        return getImage(url, { headers });
    }
    return null;
};

/**
 * @param {string?} locationId
 * @param {string?} partnerId
 * @returns {Promise<string?>}
 */
export const getPartnerLogo = async (locationId, partnerId) => {
    if (locationId) {
        const logoFromProductEndpoint = await getProductImage(locationId);
        if (logoFromProductEndpoint) {
            return logoFromProductEndpoint;
        }
    }

    if (partnerId) {
        return `/bin/partner-logo?partnerId=${partnerId.replaceAll('-', '')}`;
    }
    return null;
};

/**
 * Attempt to load operating company logo from each location with a matching APR code,
 * then fall back to parent logo.
 * @param {{ apiData: Object, aprCode: string, parentLocationId?: string?, parentPartnerId?: string? }} params
 */
export const getOpCoLogo = async ({
    apiData, aprCode, parentLocationId = null, parentPartnerId = null
}) => {
    const aprLocationIds = getFieldAsArray(apiData, Fields.Distributor.AprCode)
        .map((locationApr, arrayIndex) => ({ locationApr, locationId: getField(apiData, Fields.Distributor.LocationId, arrayIndex) }))
        .filter(({ locationApr }) => locationApr === aprCode)
        .map(({ locationId }) => locationId);

    for (const locationId of aprLocationIds) { // eslint-disable-line no-restricted-syntax
        const opCoLogo = await getProductImage(locationId); // eslint-disable-line no-await-in-loop
        if (opCoLogo) {
            return opCoLogo;
        }
    }

    return getPartnerLogo(parentLocationId, parentPartnerId);
};

export const getPortalImage = async (partnerId) => {
    const url = new URL(replaceRequestId(getConfig().partnerLogoUrl, partnerId).replace('/logos', '/images/portal'));
    const headers = {
        'Content-Type': 'application/json',
        correlation_id: getConfig().partnerLogoCorrelationId,
        client_id: getConfig().partnerLogoClientId,
        client_secret: getConfig().partnerLogoClientSecret,
    };
    return getImage(url, { headers });
};

/**
 * Gated content form handler
 * - Runs when an email button is pressed
 * - Reads email from button data attribute and fills it into form
 * @param {MouseEvent} ev
 */
export const openGatedForm = (ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    const targetEl = ev.currentTarget;
    const { email, name } = targetEl.dataset;
    const { gatedForm } = getConfig();
    const gateIdWithoutHash = gatedForm.slice(6); // Remove #gate-

    // Simplified, unified pre-fill approach using working diagnostic logic
    const performLocationPreFill = () => {
        
        const locatorSearch = targetEl.closest('locator-search');
        if (!locatorSearch) return;
        
        // Get location data
        const currentLocation = locatorSearch.state.location;
        const currentUserLocation = locatorSearch.state.userLocation;
        
        
        // Determine which location to use
        let locationToUse = null;
        if (currentLocation && (currentLocation.country || currentLocation.countrySubdivisionName || currentLocation.municipality)) {
            locationToUse = currentLocation;
        } else if (currentUserLocation && (currentUserLocation.country || currentUserLocation.countrySubdivisionName || currentUserLocation.municipality)) {
            locationToUse = currentUserLocation;
        }
        
        if (!locationToUse) {
            return;
        }
        
        const country = locationToUse.country;
        const city = locationToUse.municipality;
        const state = locationToUse.countrySubdivisionName;
        
        if (window.ff && typeof window.ff.disableCountryDetection === 'function') {
            window.ff.disableCountryDetection();
        }
        
        // Pre-fill Country (using exact working diagnostic logic)
        if (country) {
            const preFillCountry = () => {
                const countryInput = document.querySelector('input[name="$Country"]');
                const countryAutocomplete = document.querySelector('[id*="Country"][id*="listbox"]');
                
                if (!countryInput || !countryAutocomplete) {
                    return false;
                }
                
                countryInput.focus();
                countryInput.value = country;
                
                ['input', 'keyup', 'focus', 'change'].forEach(eventType => {
                    countryInput.dispatchEvent(new Event(eventType, { bubbles: true }));
                });
                
                setTimeout(() => {
                    const countryOption = Array.from(countryAutocomplete.querySelectorAll('a[role="option"]')).find(option => 
                        option.textContent.toLowerCase().trim() === country.toLowerCase().trim()
                    );
                    
                    if (countryOption) {
                        countryInput.setAttribute('aria-activedescendant', countryOption.id);
                        countryOption.setAttribute('aria-selected', 'true');
                        countryOption.click();
                    }
                }, 100);
                
                return true;
            };
            
            // Retry country pre-fill with delays
            if (!preFillCountry()) {
                setTimeout(() => {
                    if (!preFillCountry()) {
                        setTimeout(preFillCountry, 1000);
                    }
                }, 500);
            }
        }
        
        // Pre-fill City (using exact working diagnostic logic)
        if (city) {
            const preFillCity = () => {
                const cityInput = document.querySelector('input[name="$City"]');
                if (!cityInput) {
                    return false;
                }
                
                cityInput.focus();
                cityInput.value = city;
                
                ['input', 'keyup', 'focus', 'change', 'blur'].forEach(eventType => {
                    cityInput.dispatchEvent(new Event(eventType, { bubbles: true }));
                });
                
                return true;
            };
            
            // Delay city pre-fill to avoid conflicts
            setTimeout(() => {
                if (!preFillCity()) {
                    setTimeout(preFillCity, 300);
                }
            }, 300);
        }
        
        // Pre-fill State (simplified version)
        if (state) {
            setTimeout(() => {
                const stateSelect = document.querySelector('select[name="$StateorProvince"]');
                if (stateSelect && !stateSelect.closest('.hidden')) {
                    const matchingOption = Array.from(stateSelect.options).find(option => 
                        option.text.toLowerCase().trim() === state.toLowerCase().trim() ||
                        option.value.toLowerCase() === state.toLowerCase().trim() ||
                        (state.length === 2 && option.value === state.toUpperCase())
                    );
                    
                    if (matchingOption) {
                        stateSelect.value = matchingOption.value;
                        stateSelect.dispatchEvent(new Event('change', { bubbles: true }));
                    }
                }
            }, 600);
        }
    };

    // Simplified event handling - just the essentials
    window.addEventListener('gcdcModalOpened', (modalOpenEvent) => {
        
        // Handle email and name first
        const gcdcObj = modalOpenEvent.detail;
        const locatorGate = gcdcObj.GateInstances.find((gateInstance) => gateInstance.id === gateIdWithoutHash);
        if (locatorGate) {
            if (email) {
                locatorGate.form.getFieldByName('$NotificationEmailAddress').setValue(email, true);
            }
            if (name) {
                locatorGate.form.getFieldByName('$locatorPartnerName').setValue(name, true);
            }
            const locatorSearch = targetEl.closest('locator-search');
            if (locatorSearch) {
                const locationInfo = `${locatorSearch.elements.locationSelectText.textContent};${locatorSearch.readLocationState()}`;
                locatorGate.form.getFieldByName('$locatorLocation').setValue(locationInfo, true);
            }
        }
        
        // Then handle location pre-fill with delay to let form settle
        setTimeout(performLocationPreFill, 200);
    }, { once: false });

    // Backup approach - polling for form fields
    const formCheckInterval = setInterval(() => {
        const countryInput = document.querySelector('input[name="$Country"]');
        if (countryInput) {
            performLocationPreFill();
            clearInterval(formCheckInterval);
        }
    }, 500);
    
    // Stop polling after 10 seconds
    setTimeout(() => clearInterval(formCheckInterval), 10000);

    window.addEventListener("gcdcGateReady", function (event) {
        performLocationPreFill();
    });

    document.querySelector('#locator-shared__gated-form-link').click();
    targetEl.dispatchEvent(new CustomEvent('locator__email-clicked', { detail: targetEl.dataset, bubbles: true }));
};

// ==========
// HELPER FUNCTIONS AND TEMPLATES
// ==========
export const matchMobile = window.matchMedia(`(max-width: ${ROCKWELL_BREAKPOINTS_MAX_EM.DESKTOP}em)`);

/** @param {MouseEvent} ev */
export const openContactDialog = (ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    const targetEl = ev.currentTarget;
    const { title, phone } = targetEl.dataset;
    const contactDialog = document.querySelector('#contact-dialog__dialog');
    fillTemplate(contactDialog, 'data-xt', {
        title,
        phone,
        telLink: `tel:${phone}`
    }, { keepIfEmpty: true });
    contactDialog.show();
    targetEl.dispatchEvent(new CustomEvent('locator__phone-clicked', { detail: targetEl.dataset, bubbles: true }));
};

// ==========
// LOCATION HELPERS
// ==========
/**
 * The Microsoft.Maps.Search module only works if you have a map on the page,
 * which is why the REST endpoint is used here instead.
 *
 * @param {number} latitude
 * @param {number} longitude
 * @returns {Promise<string>}
 */
const getLocationName = async (latitude, longitude) => {
    if (latitude == null || longitude == null) {
        return '';
    }
    const apiUrl = new URL('https://atlas.microsoft.com/search/address/reverse/json');
    apiUrl.searchParams.set('subscription-key', getConfig().bingMapsKey);
    apiUrl.searchParams.set('api-version', '1.0');
    apiUrl.searchParams.set('query', `${latitude},${longitude}`);
    const response = await fetch(apiUrl);
    const json = await response.json();

    const address = json?.addresses?.[0]?.address;
    const displayAddress = [
        address?.municipality,
        address?.countrySubdivisionName || address?.countrySubdivisionCode,
        address?.country
    ];
    return displayAddress.filter((part) => part != null).join(', ');
};

const LOCATION_STORAGE_KEY = 'ra_locator_search_location';

const getLocationCoordinates = async (locationName) => {
    const apiUrl = new URL('https://atlas.microsoft.com/search/address/json');
    apiUrl.searchParams.set('subscription-key', getConfig().bingMapsKey);
    apiUrl.searchParams.set('api-version', '1.0');
    apiUrl.searchParams.set('query', locationName);
    const response = await fetch(apiUrl);
    const storedLocation = JSON.parse(window.localStorage.getItem(LOCATION_STORAGE_KEY));
    const json = await response.json();
    if (response.ok && json.results && json.results[0] && json.results[0].position) {
        return json.results[0].position;
    }
    return {
        lat: storedLocation?.latitude,
        lon: storedLocation?.longitude
    };
};

/** @returns {{ name: string?, latitude: number?, longitude: number?, country: string?, countrySubdivisionName: string?, municipality: string? }} */
export const readStoredLocation = async () => {
    let loggedInData = JSON.parse(window.localStorage.getItem('loggedInData'));
    if (loggedInData && loggedInData.address && loggedInData.city && loggedInData.region && loggedInData.country) {
        const loggedInDataAddress = [
            loggedInData.city,
            loggedInData.region,
            loggedInData.country
        ];
        const locationName = loggedInDataAddress.join(', ');
        const coordinates = await getLocationCoordinates(loggedInDataAddress);
        return {
            name: locationName,
            latitude: coordinates.lat,
            longitude: coordinates.lon,
            country: loggedInData.country,
            countrySubdivisionName: loggedInData.region,
            municipality: loggedInData.city,
        }
    } else {
        const storedLocation = JSON.parse(window.localStorage.getItem(LOCATION_STORAGE_KEY));
        return {
            name: storedLocation?.name,
            latitude: storedLocation?.latitude,
            longitude: storedLocation?.longitude,
            country: storedLocation?.country,
            countrySubdivisionName: storedLocation?.countrySubdivisionName,
            municipality: storedLocation?.municipality
        };
    }
};

/** @param {{ name: string?, latitude: number?, longitude: number?, country: string?, countrySubdivisionName: string?, municipality: string? }} location */
export const writeStoredLocation = (location) => {
    window.localStorage.setItem(LOCATION_STORAGE_KEY, JSON.stringify(location));
};

/**
 * @param {boolean?} getName
 * @returns {Promise<{ name: string?, latitude: number?, longitude: number?, country: string?, countrySubdivisionName: string?, municipality: string? }>}
 */
export const readUserLocation = async (getName = false) => {
    // Wait for getPiraObj to be available with timeout
    const piraObj = await waitForPiraObj();
    
    let name = null;
    let municipality = null;
    let countrySubdivisionName = null;
    let country = null;
    
    if (getName && piraObj?.latitude != null && piraObj?.longitude != null) {
        name = await getLocationName(piraObj.latitude, piraObj.longitude);
        
        // Parse the name string (format: "municipality, countrySubdivisionName, country")
        if (name) {
            const parts = name.split(',').map(part => part.trim()).filter(part => part !== '');
            municipality = parts[0] || null;
            countrySubdivisionName = parts[1] || null;
            country = parts[2] || null;
        }
    }
    
    return {
        name,
        latitude: piraObj?.latitude,
        longitude: piraObj?.longitude,
        country,
        countrySubdivisionName,
        municipality,
    };
};

/**
 * Wait for getPiraObj to be available before proceeding
 * @param {number} timeout - Maximum time to wait in milliseconds (default: 4000ms)
 * @returns {Promise<Object?>} Resolves with piraObj when getPiraObj is available, null on timeout
 */
const waitForPiraObj = async (timeout = 4000) => {
    return new Promise((resolve) => {
        const startTime = Date.now();
        
        const checkPiraObj = () => {
            if (typeof window.getPiraObj === 'function') {
                resolve(window.getPiraObj());
            } else if (Date.now() - startTime > timeout) {
                console.warn('getPiraObj not available after timeout, returning null');
                resolve(null);
            } else {
                // Check again in 1000ms
                setTimeout(checkPiraObj, 1000);
            }
        };
        checkPiraObj();
    });
};

/**
 * Haversine formula
 * @param {{ latitude: number?, longitude: number? }} location1
 * @param {{ latitude: number?, longitude: number? }} location2
 * @returns {number}
 */
export const calcGeoDistance = (location1, location2) => {
    if (location1.latitude === location2.latitude && location1.longitude === location2.longitude) {
        return 0;
    }

    const radLat1 = (Math.PI * location1.latitude) / 180;
    const radLat2 = (Math.PI * location2.latitude) / 180;
    const theta = location1.longitude - location2.longitude;
    const radTheta = (Math.PI * theta) / 180;

    let dist = Math.sin(radLat1) * Math.sin(radLat2)
        + Math.cos(radLat1) * Math.cos(radLat2) * Math.cos(radTheta);

    if (dist > 1) {
        dist = 1;
    }

    dist = ((Math.acos(dist) * 180) / Math.PI) * 60 * 1.1515;
    return dist;
};

/**
 * @param {Object} apiData
 * @param {string} aprCode
 * @param {{ latitude?: number, longitude?: number }?} userLocation - User location
 * @returns {number} The index of the nearest location with matching APR
 */
export const getNearestOpCoIndex = (apiData, aprCode, userLocation) => {

    // Get indexes of all locations that match the APR code
    const opCoIdxs = getFieldAsArray(apiData, Fields.Distributor.AprCode)
        .map((distAprCode, index) => (distAprCode === aprCode ? index : -1))
        .filter((index) => index !== -1);

    // If a user location is given, find the closest out of the matching locations
    if (userLocation?.latitude && userLocation?.longitude) {
        const latitudeArr = getFieldAsArray(apiData, Fields.Distributor.Location.Latitude);
        const longitudeArr = getFieldAsArray(apiData, Fields.Distributor.Location.Longitude);

        let closestIdx = -1;
        let closestDistance = Infinity;

        opCoIdxs.forEach((index) => {
            const thisLocation = { latitude: latitudeArr[index], longitude: longitudeArr[index] };
            const distance = calcGeoDistance(userLocation, thisLocation);
            if (distance < closestDistance) {
                closestIdx = index;
                closestDistance = distance;
            }
        });

        return closestIdx;
    }

    // If no location is given, fallback to the first matching location
    return opCoIdxs[0];
};

export const backLinkSessionStorageKey = 'filteredPageUrl';
